/**
 * Cookie Consent Constants
 * All Greek translations and configuration for the cookie consent feature
 */

import type { CookieCategory } from './types';

/**
 * Consent is managed by the production CMP loaded via GTM
 * (`cookie_set_prod_v20.js`), which renders its own banner/dialog and writes the
 * `cookie_consent_set` signal that the GTM GA4 triggers read. When true, our
 * in-house React banner/dialog do NOT render — this avoids a duplicate cookie UI
 * and a second, incompatible consent cookie (`germanos_cookie_consent`). The
 * provider/context stay mounted so existing consumers (e.g. the footer link)
 * keep compiling. Flip to false to fall back to the in-house CMP.
 */
export const CONSENT_MANAGED_BY_GTM = true;

/** Cookie storage configuration */
export const COOKIE_CONFIG = {
  /** Cookie name for storing consent */
  name: 'germanos_cookie_consent',
  /** Cookie expiry in days (1 year) */
  expiryDays: 365,
  /** Current version for migrations */
  version: '1.0',
} as const;

/** Dialog content translations */
export const DIALOG_CONTENT = {
  title: 'Ρυθμίσεις για cookies και παρόμοιες τεχνολογίες',
  description: `Χρησιμοποιούμε cookies και παρόμοιες τεχνολογίες στον ιστότοπό μας για να αποθηκεύουμε, να ανακτούμε και να επεξεργαζόμαστε πληροφορίες στη συσκευή σας. Οι συνεργάτες μας χρησιμοποιούν επίσης αυτές τις τεχνολογίες. Εμείς και οι συνεργάτες μας μπορούμε να χρησιμοποιούμε αυτές τις τεχνολογίες για σκοπούς ανάλυσης και μάρκετινγκ μόνο με τη συγκατάθεσή σας.

Χρησιμοποιήστε τους διακόπτες για να ορίσετε τη συγκατάθεσή σας και να την ανακαλέσετε οποιαδήποτε στιγμή. Μπορείτε πάντα να ανοίξετε την τρέχουσα σελίδα από το υποσέλιδο (footer) του ιστότοπού μας.

Η συγκατάθεσή σας αποτελεί επίσης τη βάση για τη μεταφορά δεδομένων σε χώρες εκτός ΕΕ (Άρθρο 49 (1) α του ΓΚΠΔ). Η ΓΕΡΜΑΝΟΣ ΑΒΕΕ δεν μπορεί να εξασφαλίσει το ίδιο επίπεδο προστασίας δεδομένων όπως στην ΕΕ. Υπάρχει η πιθανότητα, για παράδειγμα, οι τοπικές αρχές να έχουν πρόσβαση στα δεδομένα σας. Η άσκηση των δικαιωμάτων σας για την προστασία των δεδομένων μπορεί επίσης να είναι περιορισμένη. Αν θέλετε να μάθετε περισσότερα, παρακαλούμε διαβάστε τις πληροφορίες μας για την επεξεργασία δεδομένων σε χώρες εκτός ΕΕ και τη λίστα των συνεργατών μας.`,
  links: {
    privacyPolicy: {
      label: 'Ενημέρωση Προστασίας Δεδομένων',
      url: 'https://www.telekom.gr/wcs-pages/dpn/data-privacy-notice-germanos.html',
    },
    partnerList: {
      label: 'Λίστα Συνεργατών (6)',
      url: 'https://www.telekom.gr/wcs-pages/dpn/data-privacy-notice-germanos.html#question7',
    },
  },
  buttons: {
    back: 'Επιστροφή',
    acceptSelected: 'Μόνο τα επιλεγμένα',
    acceptAll: 'Αποδοχή όλων',
  },
  toggle: {
    alwaysActive: 'Πάντα ενεργά',
    active: 'Ενεργά',
    inactive: 'Ανενεργά',
  },
  readMore: 'Διαβάστε περισσότερα',
  readLess: 'Λιγότερα',
} as const;

/** Banner content translations */
export const BANNER_CONTENT = {
  title: 'Χρησιμοποιούμε cookies',
  description: 'Χρησιμοποιούμε cookies και παρόμοιες τεχνολογίες για να βελτιώσουμε την εμπειρία σας στον ιστότοπό μας.',
  buttons: {
    settings: 'Ρυθμίσεις',
    acceptAll: 'Αποδοχή όλων',
  },
} as const;

/** Cookie categories configuration */
export const COOKIE_CATEGORIES: CookieCategory[] = [
  {
    id: 'necessary',
    title: 'Αναγκαία και λειτουργικότητας',
    description: 'Αυτά τα cookies είναι πάντα ενεργά και απαραίτητα για τη σωστή λειτουργία του ιστότοπού μας.',
    canToggle: false,
    defaultValue: true,
  },
  {
    id: 'performance',
    title: 'Επιδόσεων (για σκοπούς της ΓΕΡΜΑΝΟΣ ΑΒΕΕ)',
    description: 'Αυτά τα cookies μας βοηθούν να κατανοήσουμε καλύτερα πώς χρησιμοποιείται ο ιστότοπός μας.',
    extendedDescription: `Χρησιμοποιούμε cookies και τεχνολογίες ανάλυσης επισκεψιμότητας για να κατανοήσουμε καλύτερα πώς χρησιμοποιείται ο ιστότοπός μας. Μας βοηθούν να βελτιστοποιούμε τις ψηφιακές υπηρεσίες μας. Για παράδειγμα, μπορούμε να μετρήσουμε πόσοι χρήστες επισκέπτονται τον ιστότοπό μας ή μια συγκεκριμένη υπηρεσία. Είναι επίσης χρήσιμα για στατιστικές αναλύσεις που μας δείχνουν πώς χρησιμοποιούν οι χρήστες τις ψηφιακές υπηρεσίες μας. Η ανάλυση βασίζεται σε ψευδωνυμοποιημένες πληροφορίες.`,
    canToggle: true,
    defaultValue: true,
  },
  {
    id: 'partners',
    title: 'Επιδόσεων μέσω συνεργατών',
    description: 'Αυτά τα cookies βοηθούν εμάς και τους συνεργάτες μας να βελτιώσουμε τις υπηρεσίες μας.',
    extendedDescription: `Χρησιμοποιούμε cookies και παρόμοιες τεχνολογίες στους ιστότοπούς μας για να μας βοηθήσουν να βελτιώσουμε τις υπηρεσίες μας. Αυτές οι τεχνολογίες χρησιμοποιούνται επίσης από τους συνεργάτες μας, οι οποίοι προσφέρουν τις υπηρεσίες τους ανεξάρτητα ή με κοινή ευθύνη με την ΓΕΡΜΑΝΟΣ ΑΒΕΕ.

Δεδομένα και πληροφορίες μεταδίδονται στους συνεργάτες για σκοπούς στατιστικής ανάλυσης και μερικές φορές υποβάλλονται σε επεξεργασία για τους σκοπούς ανάλυσης του συνεργάτη και συγχωνεύονται με δεδομένα τρίτων. Αυτό σημαίνει ότι οι πληροφορίες που συλλέγουμε για εσάς και αφορούν την χρήση των ψηφιακών υπηρεσιών συλλέγονται και υποβάλλονται σε επεξεργασία, προκειμένου να κατανοήσουμε καλύτερα και να βελτιώσουμε τα προϊόντα και τις προσφορές μας.`,
    canToggle: true,
    defaultValue: true,
  },
  {
    id: 'advertising',
    title: 'Διαφήμισης μέσω συνεργατών',
    description: 'Εμείς και οι συνεργάτες μας χρησιμοποιούμε αυτά τα cookies για να σας δείχνουμε εξατομικευμένο περιεχόμενο, που είναι σχετικό με εσάς.',
    extendedDescription: `Εμείς και οι συνεργάτες μας χρησιμοποιούμε cookies και παρόμοιες τεχνολογίες μάρκετινγκ για να προσφέρουμε εξατομικευμένες διαφημίσεις. Ορισμένοι από τους συνεργάτες μας ενεργούν ως ανεξάρτητοι υπεύθυνοι επεξεργασίας ή ως από κοινού υπεύθυνοι επεξεργασίας με την ΓΕΡΜΑΝΟΣ ΑΒΕΕ. Χρησιμοποιούν τα cookies και άλλες παρόμοιες τεχνολογίες για σκοπούς marketing. Τα δεδομένα μπορούν να χρησιμοποιηθούν για διαφημιστικούς σκοπούς των συνεργατών, να υποβληθούν σε περαιτέρω επεξεργασία και να συνδυαστούν με δεδομένα από άλλες πηγές.

Όταν χρησιμοποιείτε τον ιστότοπό μας, δημιουργούμε ένα προφίλ. Αυτό μας βοηθά να σας δείχνουμε διαφημίσεις σε άλλους ιστότοπους που είναι προσαρμοσμένες στα ενδιαφέροντά σας. Μετράμε επίσης την αποτελεσματικότητα αυτής της διαφήμισης και συλλέγουμε περαιτέρω πληροφορίες σχετικά με τη διαδικτυακή σας συμπεριφορά.`,
    canToggle: true,
    defaultValue: true,
  },
] as const;

/** Default preferences (all enabled) */
export const DEFAULT_PREFERENCES = {
  necessary: true as const,
  performance: true,
  partners: true,
  advertising: true,
};
